import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useRef
} from "react";
import { isSameMessage } from "./utils";
import {
  ChatMessage,
  MessageLikeStatus,
  MAX_MESSAGES
} from "@/entities/messaging/types";
import { messagesCompareFn } from "./utils";
import {
  withoutAnsweredErrorCards,
  withoutFailedAnswerTo
} from "@/components/global/Chat/components/chat/chatSlicer";

// Ids the server gives stored messages. A question still under a client-side
// id never reached the server, so there is nothing stored to re-ask.
export const STORED_MESSAGE_ID_PREFIX = "dm-";

// A question kept on screen under its client-side id when its answer timed out.
const isKeptOptimisticQuestion = (message: ChatMessage) =>
  !!message.requestId && !message.id.startsWith(STORED_MESSAGE_ID_PREFIX);

type ChatContextType = {
  messages: ChatMessage[];
  pendingUserMessage: ChatMessage | null;
  addMessages: (newMessages: ChatMessage[]) => void;
  removeMessage: (messageId: string) => void;
  dismissFailedAnswer: (questionId: string) => void;
  setPendingUserMessage: (message: ChatMessage | null) => void;
  updateMessageState: (
    starredMessage: ChatMessage,
    starredState?: boolean,
    likeState?: MessageLikeStatus
  ) => void;
  clearMessages: () => void;
  updateMessageReport: (
    messageId: string,
    report: Partial<Pick<ChatMessage, "savedReportId" | "scheduleId">>
  ) => void;
  forgetSavedReport: (reportId: string) => void;
  setSavedReportScheduled: (reportId: string, isScheduled: boolean) => void;
  renameSavedReport: (reportId: string, name: string) => void;
  waitingForMessage: boolean;
  setWaitingForMessage: (waiting: boolean) => void;
  thoughtMessage: string;
  setThoughtMessage: (thought: string) => void;
  pendingRequestId: string | null;
  setPendingRequestId: (requestId: string | null) => void;
  lastResponseRequestId: string | null;
  onResponseReceived: (requestId: string) => void;
  isCancelling: boolean;
  setIsCancelling: (cancelling: boolean) => void;
  stoppedRequestIds: Set<string>;
  addStoppedRequestId: (requestId: string) => void;
  markMessageStopped: (messageId: string) => void;
  markResponsesStoppedByRequestId: (requestId: string) => void;
  markLastUserMessageStopped: (userId: string) => void;
  // The question a retry is re-asking. It is waited on however old it is,
  // since its answer is now in flight.
  retryingQuestionId: string | null;
  setRetryingQuestionId: (messageId: string | null) => void;
};

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export function ChatProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [pendingUserMessage, setPendingUserMessage] =
    useState<ChatMessage | null>(null);
  const [waitingForMessage, setWaitingForMessage] = useState(false);
  const [thoughtMessage, setThoughtMessage] = useState("");
  const [pendingRequestId, setPendingRequestId] = useState<string | null>(null);
  const [lastResponseRequestId, setLastResponseRequestId] = useState<
    string | null
  >(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [retryingQuestionId, setRetryingQuestionId] = useState<string | null>(
    null
  );
  const [stoppedRequestIds, setStoppedRequestIds] = useState<Set<string>>(
    () => new Set()
  );

  // Track seen message IDs for O(1) deduplication
  const seenIdsRef = useRef<Set<string>>(new Set());

  // Ref mirror of stoppedRequestIds for synchronous access in addMessages
  const stoppedRequestIdsRef = useRef<Set<string>>(new Set());

  // Keeps the newest MAX_MESSAGES and the seen-id set in step with them.
  const retainLatest = (merged: ChatMessage[]): ChatMessage[] => {
    if (merged.length <= MAX_MESSAGES) return merged;
    const trimmed = merged.slice(-MAX_MESSAGES);
    seenIdsRef.current = new Set(trimmed.map((msg) => msg.id));
    return trimmed;
  };

  const addMessages = useCallback((newMessages: ChatMessage[]) => {
    if (newMessages.length === 0) return;

    setMessages((prevMessages) => {
      // Build set of existing message IDs from current state
      // This ensures we always deduplicate against the actual current state
      const existingIds = new Set(prevMessages.map((msg) => msg.id));
      const messagesToAdd: ChatMessage[] = [];
      // A regenerated draft is re-broadcast on the message it belongs to, not
      // as a new message: the server appends a version and re-sends the same
      // id. Deduplication would drop it, leaving the card waiting forever.
      const redeliveredDrafts = new Map<string, ChatMessage>();

      for (const msg of newMessages) {
        // Skip if already in current messages or already being added in this batch
        if (existingIds.has(msg.id)) {
          if (msg.emailDraft) {
            redeliveredDrafts.set(msg.id, msg);
          }
          continue;
        }

        existingIds.add(msg.id);

        // Flag late-arriving Dreamer responses for stopped requests
        const annotatedMsg =
          msg.requestId && stoppedRequestIdsRef.current.has(msg.requestId)
            ? { ...msg, isStoppedResponse: true }
            : msg;
        messagesToAdd.push(annotatedMsg);
      }

      // Also update the seenIds ref for consistency
      for (const msg of messagesToAdd) {
        seenIdsRef.current.add(msg.id);
      }

      const withRedeliveredDrafts =
        redeliveredDrafts.size === 0
          ? prevMessages
          : prevMessages.map((msg) => {
              const redelivered = redeliveredDrafts.get(msg.id);
              if (!redelivered) return msg;
              // `message` moves with the draft: a regenerate re-renders Dreamer's opening
              // for the new version, so keeping the old text would leave the intro above
              // the card describing a draft the user is no longer looking at.
              return {
                ...msg,
                message: redelivered.message,
                emailDraft: redelivered.emailDraft
              };
            });

      if (messagesToAdd.length === 0) {
        return withRedeliveredDrafts;
      }

      // The stored copy of a question kept after a timeout (e.g. history sent
      // again on reconnect) replaces the kept copy, matched as hasPendingEcho
      // matches a pending one: same sender, same text. Each stored copy replaces
      // one kept copy, the oldest, so a repeat of the question that never
      // reached the server stays.
      const storedCounts = new Map<string, number>();
      for (const message of messagesToAdd) {
        if (!message.id.startsWith(STORED_MESSAGE_ID_PREFIX)) continue;
        const key = `${message.user?.id}\u0000${message.message}`;
        storedCounts.set(key, (storedCounts.get(key) ?? 0) + 1);
      }
      const withoutReplacedCopies =
        storedCounts.size === 0
          ? withRedeliveredDrafts
          : withRedeliveredDrafts.filter((message) => {
              if (!isKeptOptimisticQuestion(message)) return true;
              const key = `${message.user?.id}\u0000${message.message}`;
              const remaining = storedCounts.get(key) ?? 0;
              if (remaining === 0) return true;
              storedCounts.set(key, remaining - 1);
              return false;
            });

      // Merge and sort
      const merged = [...withoutReplacedCopies, ...messagesToAdd];
      merged.sort(messagesCompareFn);
      return retainLatest(withoutAnsweredErrorCards(merged));
    });

    // Clear pending user message when we receive messages from server
    // The server echo will be in the newMessages, so we can clear the optimistic version
    setPendingUserMessage(null);
  }, []);

  // The id stays seen, so a late redelivery of the removed message is dropped.
  const removeMessage = useCallback((messageId: string) => {
    setMessages((prevMessages) =>
      prevMessages.some((message) => message.id === messageId)
        ? prevMessages.filter((message) => message.id !== messageId)
        : prevMessages
    );
  }, []);

  const dismissFailedAnswer = useCallback((questionId: string) => {
    setMessages((prevMessages) =>
      withoutFailedAnswerTo(prevMessages, questionId)
    );
  }, []);

  const onResponseReceived = useCallback((requestId: string) => {
    setLastResponseRequestId(requestId);
    setPendingRequestId((current) => (current === requestId ? null : current));
  }, []);

  const updateMessageState = (
    targetMessage: ChatMessage,
    starredState?: boolean,
    likeState?: MessageLikeStatus
  ) => {
    setMessages((prevMessages) =>
      prevMessages.map((msg) =>
        isSameMessage(msg, targetMessage)
          ? {
              ...msg,
              ...(starredState !== undefined && { isStarred: starredState }),
              ...(likeState !== undefined && { isLiked: likeState })
            }
          : msg
      )
    );
  };
  // The server computes a message's saved report when it sends it; after that,
  // saves and removals from the chat itself are recorded here, so an answer
  // re-rendered later (a tab switch remounts the list) still knows it is saved.
  const updateMessageReport = useCallback(
    (
      messageId: string,
      report: Partial<Pick<ChatMessage, "savedReportId" | "scheduleId">>
    ) => {
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === messageId ? { ...msg, ...report } : msg
        )
      );
    },
    []
  );

  // A report's runs are messages of their own that name the same report, so a
  // removal clears it from every one of them, not only the answer it came from.
  // The name goes with the ids: it is the removed report's, and left behind it
  // would title a card that is no longer saved — and name its download — until
  // the next refetch, where the generated title is what should show.
  const forgetSavedReport = useCallback((reportId: string) => {
    setMessages((prevMessages) =>
      prevMessages.map((msg) =>
        msg.savedReportId === reportId || msg.scheduleId === reportId
          ? { ...msg, savedReportId: null, scheduleId: null, reportName: null }
          : msg
      )
    );
  }, []);

  // A schedule set or cleared away from its answer — from the Reports tab —
  // is recorded on every message naming the report, as a removal is.
  const setSavedReportScheduled = useCallback(
    (reportId: string, isScheduled: boolean) => {
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.savedReportId === reportId || msg.scheduleId === reportId
            ? { ...msg, scheduleId: isScheduled ? reportId : null }
            : msg
        )
      );
    },
    []
  );

  // A rename from the Reports tab is recorded on every message naming the
  // report, as a removal is: the card and its download are named from
  // reportName, so otherwise the chat keeps the old name until the next refetch.
  const renameSavedReport = useCallback((reportId: string, name: string) => {
    setMessages((previousMessages) =>
      previousMessages.map((message) =>
        message.savedReportId === reportId || message.scheduleId === reportId
          ? { ...message, reportName: name }
          : message
      )
    );
  }, []);

  const addStoppedRequestId = useCallback((requestId: string) => {
    // Update ref first for synchronous access in addMessages
    stoppedRequestIdsRef.current.add(requestId);
    setStoppedRequestIds((current) => {
      const next = new Set(current);
      next.add(requestId);
      return next;
    });
  }, []);

  const markMessageStopped = useCallback((messageId: string) => {
    setMessages((prevMessages) =>
      prevMessages.map((msg) =>
        msg.id === messageId ? { ...msg, isStopped: true } : msg
      )
    );
  }, []);

  const markResponsesStoppedByRequestId = useCallback(
    (requestId: string) => {
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.requestId === requestId && !msg.isStopped
            ? { ...msg, isStoppedResponse: true }
            : msg
        )
      );
    },
    []
  );

  const markLastUserMessageStopped = useCallback((userId: string) => {
    setMessages((prevMessages) => {
      for (let i = prevMessages.length - 1; i >= 0; i--) {
        if (prevMessages[i].user?.id === userId) {
          const updated = [...prevMessages];
          updated[i] = { ...updated[i], isStopped: true };
          return updated;
        }
      }
      return prevMessages;
    });
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    seenIdsRef.current.clear();
    stoppedRequestIdsRef.current.clear();
    setStoppedRequestIds(new Set());
    setRetryingQuestionId(null);
  }, []);

  return (
    <ChatContext.Provider
      value={{
        messages,
        pendingUserMessage,
        addMessages,
        removeMessage,
        dismissFailedAnswer,
        setPendingUserMessage,
        updateMessageState,
        clearMessages,
        updateMessageReport,
        forgetSavedReport,
        setSavedReportScheduled,
        renameSavedReport,
        waitingForMessage,
        setWaitingForMessage,
        thoughtMessage,
        setThoughtMessage,
        pendingRequestId,
        setPendingRequestId,
        lastResponseRequestId,
        onResponseReceived,
        isCancelling,
        setIsCancelling,
        stoppedRequestIds,
        addStoppedRequestId,
        markMessageStopped,
        markResponsesStoppedByRequestId,
        markLastUserMessageStopped,
        retryingQuestionId,
        setRetryingQuestionId
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
}
